import React, { useEffect, useState } from "react";
import styled from "styled-components";
import QuestionLabel from "./components/question_label";
import { QuestionProps } from "../survey";
import DatePickerComponent from "../../components/data_entry/./deprecated_date_picker";
import nextId from "react-id-generator";
import DatepickerQuestionWrapper from "../../components/base_ui/higher_order_components/questions/datepicker_question_wrapper";

export type Answer = string;

export interface Customizations {
  prompt: string;
  description?: string;
  optional?: boolean;
  minDateDaysRelativeToToday?: number;
  maxDateDaysRelativeToToday?: number;
}

const StyledDatePicker = styled.div`
  text-transform: none;
  display: flex;
  flex-direction: column;
  align-items: center;

  .calendar {
    margin-top: ${({ theme }) => theme.sizing.scale300};
  }
`;

export default function SurveyQuestionDatePicker({
  customizations,
  currentAnswer,
  updateAnswer,
  setAbleToSubmit,
  showValidation,
  showRefreshedComponents = true,
}: QuestionProps<Answer, Customizations>) {
  const label = customizations.prompt;
  const id = nextId();
  const optional = typeof customizations.optional === "undefined" ? false : customizations.optional;
  const description = customizations.description;

  // set up for limiting datepicker by a range
  let minDate: Date | undefined;
  if (typeof customizations.minDateDaysRelativeToToday === "number") {
    minDate = new Date();
    minDate.setDate(minDate.getDate() + customizations.minDateDaysRelativeToToday);
    minDate.setHours(0, 0, 0, 0);
  }

  let maxDate: Date | undefined;
  if (typeof customizations.maxDateDaysRelativeToToday === "number") {
    maxDate = new Date();
    maxDate.setDate(maxDate.getDate() + customizations.maxDateDaysRelativeToToday);
    maxDate.setHours(0, 0, 0, 0);
  }

  const date =
    // if a day is unselected, the answer will be ""
    currentAnswer && currentAnswer.length > 0 ? new Date(currentAnswer) : undefined;

  const answerIsValidRequired = optional || date !== undefined;

  // TODO: remove showSurveyRefresh later when we remove deprecatedDatepicker - avoiding
  // checking the date validation here because the old component does not support it
  const answerIsValidDate =
    !showRefreshedComponents ||
    (date !== undefined &&
      (minDate === undefined || date >= minDate) &&
      (maxDate === undefined || date <= maxDate));

  useEffect(() => {
    setAbleToSubmit(answerIsValidRequired && answerIsValidDate);
  }, [answerIsValidRequired, answerIsValidDate, setAbleToSubmit]);

  const [datepickerErrorMessage, setDatepickerErrorMessage] = useState<string | undefined>(
    undefined,
  );
  useEffect(() => {
    const requiredErrorMessage = "Please complete the required field.";
    const dateErrorMessage =
      minDate && maxDate
        ? `Please select a date between ${minDate.toLocaleDateString()} and ${maxDate.toLocaleDateString()}.`
        : minDate
          ? `Please select a date on or after ${minDate.toLocaleDateString()}.`
          : maxDate
            ? `Please select a date on or before ${maxDate.toLocaleDateString()}.`
            : undefined;

    if (showValidation) {
      setDatepickerErrorMessage(
        !answerIsValidRequired
          ? requiredErrorMessage
          : !answerIsValidDate
            ? dateErrorMessage
            : undefined,
      );
    }
  }, [showValidation, answerIsValidRequired, answerIsValidDate, minDate, maxDate]);

  const deprecatedDatepicker = (
    <StyledDatePicker>
      <QuestionLabel
        label={label}
        htmlFor={id}
        description={description}
        data-test="datepicker-question-label"
      />
      <DatePickerComponent
        id={id}
        className="calendar"
        selectedDays={[date]}
        // eslint-disable-next-line
        // @ts-ignore this is a problem w/ the react-day-picker types
        onDayClick={(day: Date, { selected }: { selected: boolean }) =>
          updateAnswer(selected ? "" : day.toString())
        }
        error={datepickerErrorMessage}
      />
    </StyledDatePicker>
  );

  const datepicker = (
    <DatepickerQuestionWrapper
      supplementalQuestionPrompt={label}
      secondaryText={description}
      label={label}
      optional={optional}
      errorMessage={datepickerErrorMessage}
      dataTest={"datepicker-question"}
      minDate={minDate}
      maxDate={maxDate}
      value={[date]}
      onChange={(newValue) => updateAnswer(newValue.date ? newValue.date.toString() : "")}
    />
  );

  return showRefreshedComponents ? datepicker : deprecatedDatepicker;
}
